import { MilestoneView, Rarity } from "@highmarch/domain";
import type { LevelCatalog } from "./levelCatalog";
import { Modes } from "./modes";

/**
 * The free routes to gear power (gear v2, docs/GEAR_V2.md §Free routes) as one claim list for the hub: the Endless
 * weekly milestones, the Iron star totals, and what the achievements and event tracks hold of the forge's goods. The
 * claims themselves go through the owning use case (Modes.claimEndlessMilestone / claimIronMilestone, the
 * Achievements and Events claims); this is the read model the reward screens and the badge share.
 */

export type FreeRouteSource = "endless" | "iron";

export interface FreeRouteRow extends MilestoneView {
  readonly source: FreeRouteSource;
  /** "Wave 30 this week", "15 Iron stars". */
  readonly label: string;
}

export interface FreeRoutes {
  readonly rows: readonly FreeRouteRow[];
  /** Rows reached and not yet claimed (the badge). */
  readonly claimable: number;
  /** Forge goods held now: essence and sigils by rarity. */
  readonly essence: number;
  readonly sigils: readonly number[];
}

export function freeRoutes(catalog: LevelCatalog, now: number): FreeRoutes {
  const modes = new Modes(catalog);
  const rows: FreeRouteRow[] = [
    ...modes.endlessMilestones(now).map((m): FreeRouteRow => ({ ...m, source: "endless", label: `Wave ${m.milestone.count}${m.week === modes.state.week ? " this week" : " last week"}` })),
    ...(modes.ironUnlocked || modes.state.ironStarCount > 0 ? modes.ironMilestones() : []).map((m): FreeRouteRow => ({ ...m, source: "iron", label: `${m.milestone.count} Iron star${m.milestone.count === 1 ? "" : "s"}` })),
  ];
  return { rows, claimable: rows.filter((r) => r.reached && !r.claimed).length, essence: catalog.profile.forge.essence, sigils: [...catalog.profile.forge.sigils] };
}

/** A reward's forge goods in words ("120 essence · 1 Legendary sigil"), for a claim row. */
export function forgeGoods(r: { readonly essence: number; readonly sigil: Rarity | null; readonly sigils: number }): string {
  const parts: string[] = [];
  if (r.essence > 0) parts.push(`${r.essence} essence`);
  if (r.sigil !== null && r.sigils > 0) parts.push(`${r.sigils} ${Rarity[r.sigil]} sigil${r.sigils === 1 ? "" : "s"}`);
  return parts.join(" · ");
}
